import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ToastItem {
  id: string;
  /** A notification type (translated via `notifications.<kind>`) or a plain literal message. */
  kind: string;
  actor?: string | null;
  subject?: string | null;
  /** Set for toasts that aren't a server notification (e.g. a form error). */
  literal?: string;
}

interface ToastContextValue {
  toasts: ToastItem[];
  pushToast: (toast: ToastItem) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback(
    (toast: ToastItem) => {
      setToasts((current) => [...current.filter((existing) => existing.id !== toast.id), toast]);
      setTimeout(() => dismissToast(toast.id), AUTO_DISMISS_MS);
    },
    [dismissToast],
  );

  const value = useMemo(
    () => ({ toasts, pushToast, dismissToast }),
    [toasts, pushToast, dismissToast],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
